import * as React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
  Button,
  Spinner,
  Text,
  Badge,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { Integration } from '../../../types/integration';

const useStyles = makeStyles({
  container: {
    width: '100%',
  },
  table: {
    // Tabela visível apenas em desktop
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileCards: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  card: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardEmpresa: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  cardErp: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  cardUrl: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    lineHeight: tokens.lineHeightBase200,
  },
  cardUsuario: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardBadge: {
    flexShrink: 0,
  },
  cardActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  // Estilos para controlar largura das colunas
  empresaCell: {
    width: '18%',
    minWidth: '140px',
  },
  erpCell: {
    width: '12%',
    minWidth: '100px',
  },
  urlCell: {
    width: '30%',
    minWidth: '180px',
    maxWidth: '250px',
  },
  usuarioCell: {
    width: '10%',
    minWidth: '90px',
  },
  statusCell: {
    width: '20%',
    minWidth: '120px',
  },
  actionsHeaderCell: {
    width: '10%',
    minWidth: '120px',
  },
  // Estilos para truncar texto longo
  urlText: {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
});

interface IntegrationGridProps {
  integrations: Integration[];
  onEdit: (integration: Integration) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  error?: string | null;
}

const IntegrationGrid: React.FC<IntegrationGridProps> = ({
  integrations,
  onEdit,
  onDelete,
  loading,
  error,
}) => {
  const styles = useStyles();

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1: return 'warning'; // Senha inválida      
      case 2: return 'severe'; // Erro
      case 3: return 'success'; // Confirmado
      case 4: return 'brand'; // SemAutorizacao
      case 5: return 'danger'; // FalhaAoIntegrar
      default: return 'brand';
    }
  };

  const getStatusAppearance = (status: number) => {
    switch (status) {
      case 1: return 'filled'; // Ativa
      case 0: return 'outline'; // Inativa
      default: return 'filled';
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Carregando integrações..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text>Erro ao carregar integrações: {error}</Text>
      </div>
    );
  }

  const columns = [
    { key: 'empresa', label: 'Empresa' },
    { key: 'erpDescricao', label: 'ERP' },
    { key: 'urlIntegracao', label: 'URL Integração' },
    { key: 'usuarioERP', label: 'Usuário ERP' },
    { key: 'ultimoStatusDescricao', label: 'Status' },
    { key: 'acoes', label: 'Ações' },
  ];

  // Função helper para mapear chave da coluna para classe CSS
  const getColumnClass = (key: string) => {
    switch (key) {
      case 'empresa': return styles.empresaCell;
      case 'erpDescricao': return styles.erpCell;
      case 'urlIntegracao': return styles.urlCell;
      case 'usuarioERP': return styles.usuarioCell;
      case 'ultimoStatusDescricao': return styles.statusCell;
      case 'acoes': return styles.actionsHeaderCell;
      default: return '';
    }
  };

  // Função helper para renderizar conteúdo da célula
  const renderCellContent = (integration: Integration, key: string) => {
    switch (key) {
      case 'empresa':
        return integration.empresa;
      case 'erpDescricao':
        return integration.erpDescricao;
      case 'urlIntegracao':
        return (
          <Text className={styles.urlText}>
            {integration.urlIntegracao}
          </Text>
        );
      case 'usuarioERP':
        return integration.usuarioERP;
      case 'ultimoStatusDescricao':
        return (
          <Badge
            appearance={getStatusAppearance(integration.ultimoStatus)}
            color={getStatusColor(integration.ultimoStatus)}
          >
            {integration.ultimoStatusDescricao}
          </Badge>
        );
      case 'acoes':
        return (
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<Edit20Regular />}
              onClick={() => onEdit(integration)}
              size="small"
            >
              Editar
            </Button>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => onDelete(integration.id)}
              size="small"
            >
              Excluir
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Tabela para desktop */}
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={getColumnClass(column.key)}>
                {column.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {integrations.map((integration) => (
            <TableRow key={integration.id}>
              {columns.map((column) => (
                <TableCell key={column.key} className={getColumnClass(column.key)}>
                  {renderCellContent(integration, column.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Cards para mobile */}
      <div className={styles.mobileCards}>
        {integrations.map((integration) => (
          <div key={integration.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardContent}>
                <Text className={styles.cardEmpresa}>{integration.empresa}</Text>
                <Text className={styles.cardErp}>ERP: {integration.erpDescricao}</Text>
                <Text className={styles.cardUrl}>{integration.urlIntegracao}</Text>
              </div>
              <Badge
                appearance={getStatusAppearance(integration.ultimoStatus)}
                color={getStatusColor(integration.ultimoStatus)}
                className={styles.cardBadge}
              >
                {integration.ultimoStatusDescricao}
              </Badge>
            </div>
            <div className={styles.cardFooter}>
              <Text className={styles.cardUsuario}>
                Usuário: {integration.usuarioERP}
              </Text>
              <div className={styles.cardActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  onClick={() => onEdit(integration)}
                  size="small"
                >
                  Editar
                </Button>
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => onDelete(integration.id)}
                  size="small"
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IntegrationGrid;